/**
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business ops 依頼の差戻し確認モーダル。「依頼者へ差戻し」操作時に開き、差戻し理由（情報不足 / 内容不明確 / 再現・確認不可 / WFID 必要。集計用で必須・初期値なし）を選び、起票スレッドへ送るメッセージ（テンプレ初期値）を人手で編集・承認してから送信する。スレッドには選んだ理由のラベルも添えて返信される。送信で state は blocked（差戻し）になり、以後のやり取りは Slack スレッドで直接行う。close-report（houkoku）モーダルと同じ UI 構成で、AI ドラフトは持たない
 * @graph-connects none
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import {
  OPS_SEND_BACK_MESSAGE_MAX,
  OPS_SEND_BACK_REASONS,
  type OpsSendBackReason,
} from '@cortex/ops-core';

/** OpsSendBackModal の props。 */
export interface OpsSendBackModalProps {
  /** 対象依頼の対応者 nickName（テンプレ本文に埋め込む。未割当は null）。 */
  assigneeName: string | null;
  /** モーダルの表示状態。 */
  open: boolean;
  /** 閉じる（キャンセル）。差戻しはしない。 */
  onClose: () => void;
  /** 差戻しを確定する（差戻し理由と、起票スレッドへ送るメッセージ）。 */
  onSend: (reason: OpsSendBackReason, message: string) => void;
  /** 差戻し POST 実行中か（ボタンの disable 用）。 */
  isSending: boolean;
  /** 直前の差戻し POST が失敗したか（モーダルを開いたままエラー表示・再送信させる）。 */
  sendFailed: boolean;
}

/**
 * 差戻し確認モーダル。開いたらテンプレ（担当者名を埋め込んだ「追加情報が必要」文）を textarea の初期値にし、
 * 人手で編集・承認してから送信する。理由が未選択・メッセージが空のままでは送信を無効化する。理由は集計に使うため
 * 初期値を入れない（既定値があると選ばれていない理由が実績として残る）。送信メッセージは選んだ理由のラベルを添えて起票スレッドへ返信される。
 * @graph-connects none
 */
export function OpsSendBackModal({
  assigneeName,
  open,
  onClose,
  onSend,
  isSending,
  sendFailed,
}: OpsSendBackModalProps): ReactNode {
  const { t } = useTranslation();
  const [message, setMessage] = useState('');
  const [reason, setReason] = useState<OpsSendBackReason | null>(null);
  // この open セッションで既にテンプレ初期化したか。再レンダーで effect が再走しても入力上書きを防ぐ。
  const initializedRef = useRef(false);

  // 開いたときに 1 度だけテンプレを初期値にする。閉じたら入力をリセットする（次回開時に再初期化）。
  useEffect(() => {
    if (!open) {
      initializedRef.current = false;
      setMessage('');
      setReason(null);
      return;
    }
    if (initializedRef.current) return;
    initializedRef.current = true;
    const assignee = assigneeName ?? t('ops.detail.sendBack.assigneeFallback');
    setMessage(t('ops.detail.sendBack.template', { assignee }));
  }, [open, assigneeName, t]);

  const canSubmit = reason !== null && message.trim() !== '' && !isSending;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('ops.detail.sendBack.title')}
      description={t('ops.detail.sendBack.description')}
      size="lg"
      footer={
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={onClose} disabled={isSending}>
            {t('ops.detail.sendBack.cancel')}
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={() => {
              if (reason !== null) onSend(reason, message.trim());
            }}
            disabled={!canSubmit}
          >
            {t('ops.detail.sendBack.send')}
          </Button>
        </div>
      }
    >
      <div className="space-y-2">
        {sendFailed && (
          <p className="text-sm text-[var(--color-error)]">{t('ops.detail.sendBack.sendFailed')}</p>
        )}
        <fieldset className="space-y-1">
          <legend className="text-xs font-semibold text-[var(--color-text-secondary)]">
            {t('ops.detail.sendBack.reasonLabel')}
          </legend>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {OPS_SEND_BACK_REASONS.map((value) => (
              <label
                key={value}
                className="flex items-center gap-1.5 text-sm text-[var(--color-text-primary)]"
              >
                <input
                  type="radio"
                  name="ops-send-back-reason"
                  value={value}
                  checked={reason === value}
                  onChange={() => {
                    setReason(value);
                  }}
                  disabled={isSending}
                />
                {t(`ops.detail.sendBack.reasons.${value}`)}
              </label>
            ))}
          </div>
        </fieldset>
        <label
          htmlFor="ops-send-back"
          className="block text-xs font-semibold text-[var(--color-text-secondary)]"
        >
          {t('ops.detail.sendBack.label')}
        </label>
        <textarea
          id="ops-send-back"
          // サーバー側 schema と同じ上限（超過 400 を UI で防ぐ）。値は ops-core が単一 source。
          maxLength={OPS_SEND_BACK_MESSAGE_MAX}
          className="min-h-40 w-full resize-y rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-base)] px-3 py-2 text-sm text-[var(--color-text-primary)] focus:border-[var(--color-border-focus)] focus:outline-none"
          value={message}
          onChange={(e) => {
            setMessage(e.target.value);
          }}
          placeholder={t('ops.detail.sendBack.placeholder')}
          disabled={isSending}
        />
      </div>
    </Modal>
  );
}
